import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { generateDateTicks } from "../../../services/dataTicks";
import { SlidersHorizontal } from "lucide-react";

// Define colors for each fuel type
const fuelColors = {
  "Heavy Fuel Oil": "#1a5276", // Dark blue
  "Light Fuel Oil": "#27ae60", // Green
  "Marine Gas Oil": "#d35400", // Orange
  Others: "#7d3c98", // Purple
};

const FuelConsumptionChart = ({ data = [], isLoading = false }) => {
  const dates = data ? data.map((item) => item.recordedAt) : [];

  let tickPositions = generateDateTicks(dates);

  const [selectedFuels, setSelectedFuels] = useState([
    "Heavy Fuel Oil",
    "Light Fuel Oil",
    "Marine Gas Oil",
    "Others",
  ]);

  const handleToggleFuel = (fuel) => {
    setSelectedFuels((prev) =>
      prev.includes(fuel) ? prev.filter((f) => f !== fuel) : [...prev, fuel]
    );
  };

  // Generate scatter series data for fleet overview
  const generateSeries = () => {
    // Create a map of fuel types to their data points
    const fuelSeriesMap = {};

    // Initialize each fuel series
    Object.keys(fuelColors).forEach((fuelType) => {
      fuelSeriesMap[fuelType] = [];
    });

    // Process each day's data
    data.forEach((day) => {
      const date = new Date(day.recordedAt).getTime();

      Object.keys(fuelColors).forEach((fuelType) => {
        if (day[fuelType] !== undefined) {
          fuelSeriesMap[fuelType].push({
            x: date,
            y: day[fuelType] || 0,
            date: new Date(day.recordedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            fuelType: fuelType,
          });
        }
      });
    });

    // Create the series array for Highcharts
    return selectedFuels.map((fuelType) => {
      return {
        name: fuelType,
        type: "scatter",
        data: fuelSeriesMap[fuelType] || [],
        color: fuelColors[fuelType],
        marker: {
          symbol: "circle",
          radius: 5,
          lineWidth: 1,
          lineColor: "#fff",
          states: {
            hover: {
              enabled: true,
              lineWidth: 2,
              radius: 10,
            },
          },
        },
      };
    });
  };

  const options = {
    chart: {
      type: "scatter",
      plotBorderWidth: 1,
      height: 460,
      spacingBottom: 20,
      zooming: {
        type: "xy",
      },
      backgroundColor: "#ffffff",
      style: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
    },
    title: {
      text: null,
    },
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: {
        text: null,
      },
      labels: {
        format: "{value:%b %d}",
        style: {
          color: "#6b7280",
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#f3f4f6",
    },
    yAxis: {
      title: {
        text: "Fleet Consumption (MT)",
        style: {
          color: "#374151",
          fontWeight: "500",
        },
      },
      labels: {
        style: {
          color: "#6b7280",
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#f3f4f6",
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
    },
    tooltip: {
      useHTML: true,
      headerFormat:
        '<div style="font-size: 14px; font-weight: 600; margin-bottom: 8px">{point.date}</div>',
      pointFormat: `
        <div style="display: flex; align-items: center; margin-bottom: 4px">
          <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background-color: {series.color}; margin-right: 8px;"></span>
          <span style="font-weight: 500">{point.fuelType}</span>
        </div>
        <div style="margin-left: 20px">
          <span style="color: #6b7280">Total Consumption:</span> <span style="font-weight: 500">{point.y} MT</span>
        </div>
      `,
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderRadius: 8,
      shadow: true,
      style: {
        color: "#111827",
      },
    },
    plotOptions: {
      scatter: {
        marker: {
          states: {
            hover: {
              enabled: true,
              lineColor: "#fff",
              lineWidth: 2,
              radius: 10,
            },
          },
        },
      },
    },
    legend: {
      enabled: false,
    },
    series: generateSeries(),
    credits: {
      enabled: false,
    },
    // Added responsive configuration
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            chart: {
              height: 400,
            },
            yAxis: {
              title: {
                text: "Consumption (MT)",
              },
            },
          },
        },
      ],
    },
  };

  if (isLoading) {
    return (
      <div className="w-full h-[500px] rounded-3xl shadow-2xl flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading engine data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center gap-4">
        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#27ade3]" />
          Select by:
        </label>
        <div className="flex flex-wrap items-center justify-between gap-1.5">
          {Object.keys(fuelColors).map((fuelType) => {
            const isActive = selectedFuels.includes(fuelType);

            return (
              <button
                key={fuelType}
                className={`flex items-center px-2 py-1.5 rounded-md transition-all ${isActive
                    ? "shadow-sm"
                    : "opacity-60 hover:opacity-100"
                  }`}
                style={{
                  backgroundColor: isActive ? fuelColors[fuelType] : "transparent",
                  color: isActive ? "#ffffff" : "#6b7280"
                }}
                onClick={() => handleToggleFuel(fuelType)}
              >
                <span
                  className={`text-[13px] font-medium ${isActive ? "text-white" : "text-gray-500"
                    }`}
                >
                  {fuelType}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart container */}
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        containerProps={{ style: { width: "100%", height: "100%" } }}
      />
    </div>
  );
};

export default FuelConsumptionChart;
